/*
 * Copyright 2015-2023 Ritense BV, the Netherlands.
 *
 * Licensed under EUPL, Version 1.2 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" basis,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SelectItem } from '@valtimo/components';
import { FunctionConfigurationComponent, FunctionConfigurationData } from '@valtimo/plugin';
import { BehaviorSubject, Observable, Subscription, combineLatest, map, take } from 'rxjs';
import { ExecuteActionConfig } from '../../models';
import { TezzaPluginService } from '../../services';

@Component({
  standalone: false,
  selector: 'valtimo-execute-action-configuration',
  templateUrl: './execute-action-configuration.component.html',
  styleUrls: ['./execute-action-configuration.component.scss'],
})
export class ExecuteActionConfigurationComponent
  implements FunctionConfigurationComponent, OnInit, OnDestroy {
  @Input() save$: Observable<void>;
  @Input() disabled$: Observable<boolean>;
  @Input() pluginId: string;
  @Input() prefillConfiguration$: Observable<ExecuteActionConfig>;
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() configuration: EventEmitter<ExecuteActionConfig> =
    new EventEmitter<ExecuteActionConfig>();

  readonly actionDefinitionSelectItems$!: Observable<Array<SelectItem>>;

  private saveSubscription!: Subscription;


  private readonly formValue$ = new BehaviorSubject<ExecuteActionConfig | null>(null);
  private readonly valid$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly tezzaPluginService: TezzaPluginService
  ) {
    this.actionDefinitionSelectItems$ = this.tezzaPluginService
      .getAllActionDefinitions()
      .pipe(
        map(ActionDefinitions =>
          ActionDefinitions.map(ActionDefinition => ({
            id: ActionDefinition.entry.id,
            text: ActionDefinition.entry.name,
          }))
        )
      );
  }

  ngOnInit(): void {
    this.openSaveSubscription();
  }

  ngOnDestroy(): void {
    this.saveSubscription?.unsubscribe();
  }

  formValueChange(formValue: ExecuteActionConfig): void {
    this.formValue$.next(formValue);
    this.handleValid(formValue);
  }

  private handleValid(formValue: ExecuteActionConfig): void {
    const valid = !!(formValue.actionDefinitionId && formValue.actionParameters);

    this.valid$.next(valid);
    this.valid.emit(valid);
  }

  private openSaveSubscription(): void {
    this.saveSubscription = this.save$?.subscribe(save => {
      combineLatest([this.formValue$, this.valid$])
        .pipe(take(1))
        .subscribe(([formValue, valid]) => {
          if (valid) {
            this.configuration.emit(formValue);
          }
        });
    });
  }
}
